import type { FC, PropsWithChildren } from 'react';
import styled, { css } from 'styled-components';
import type { DataCyProp } from '@/types';
import { HeadlineLevel } from '../enums/level';
import type { StaticOptions } from '../types/StaticOptions';
import { TypographyType } from '../enums/type';

export interface HeadlineProps extends DataCyProp {
  level?: HeadlineLevel;
  type?: TypographyType.Normal | TypographyType.Inverted;
  /**
   * When used in static context, staticOptions must be provided to the component
   *  */
  staticOptions?: StaticOptions;
}

export const StyledHeadline = styled.span<Pick<HeadlineProps, 'level' | 'type'>>`
  margin: 0 !important;
  color: ${({ theme, type }) =>
    type === TypographyType.Normal ? theme.color.semantic.type.primary : theme.color.semantic.type.inverted};

  ${({ level, theme, type }) => {
    switch (level) {
      case HeadlineLevel.Two:
        return theme.typography.configurations.headline2;
      case HeadlineLevel.Three:
        return theme.typography.configurations.headline3;
      case HeadlineLevel.Four:
        return css`
          color: ${
            type === TypographyType.Normal ? theme.color.semantic.type.secondary : theme.color.semantic.type.inverted
          };
          ${theme.typography.configurations.headline4}
        `;
      default:
        return theme.typography.configurations.headline1;
    }
  }}
`;

export const LEVEL_ELEMENTS = {
  [HeadlineLevel.One]: 'h1',
  [HeadlineLevel.Two]: 'h2',
  [HeadlineLevel.Three]: 'h3',
  [HeadlineLevel.Four]: 'h4',
} as const;

export const Headline: FC<PropsWithChildren<HeadlineProps>> = ({
  children,
  level = HeadlineLevel.One,
  type = TypographyType.Normal,
  staticOptions,
  'data-cy': dataCy,
}) => {
  const { theme } = staticOptions ?? {};
  return (
    <StyledHeadline as={LEVEL_ELEMENTS[level]} data-cy={dataCy} level={level} theme={theme} type={type}>
      {children}
    </StyledHeadline>
  );
};
